/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2 } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import DeleteConfirmModal from "../../../../Common/DeleteConfirmModal";
import { getallCategory } from "../../../../Redux/actions/Operationsactions/getcategory";
import { DeleteCategoryApi } from "../../../../Api/Store/Category";

const CategoryTable = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const categoryData = useSelector((state) => state.getAllCategory);
  const rolesData = useSelector((state) => state.GetAllRole);

  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(getallCategory());
  }, [dispatch]);

  useEffect(() => {
    const allData = (categoryData?.data || []).map((c) => ({
      ...c,
      ParentCategoryName:
        (categoryData?.data || []).find((p) => p.Id === c.ParentCategoryId)?.CategoryName || "",
    }));
    const filtered = allData.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        [
          (item?.CategoryName || "").toLowerCase(),
          (item?.ParentCategoryName || "").toLowerCase(),
          (item?.Note || "").toLowerCase(),
        ].some((field) => field.includes(searchTerm.toLowerCase()));
      return matchesSearch;
    });
    setFilteredCategories(filtered);
  }, [searchTerm, categoryData?.data]);

  const handleConfirmDelete = async () => {
    if (!selectedCategory) {
      return;
    }
    try {
      setIsDeleting(true);
      const result = await DeleteCategoryApi(selectedCategory?.Id);
      if (result.data?.IsSuccess) {
        toast.success("Category deleted successfully");
        dispatch(getallCategory());
        setSelectedCategory(null);
      } else {
        toast.error(result.data?.Message || "Failed to delete category");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsDeleting(false);
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Category Name",
        field: "CategoryName",
        flex: 2,
      },
      {
        headerName: "Parent Category",
        field: "ParentCategoryName",
        flex: 2,
      },
      // {
      //   headerName: "Note",
      //   field: "Note",
      //   flex: 3,
      // },
      {
        headerName: "Actions",
        colId: "actions",
        width: 130,
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {rolesData?.data?.UserRoles?.includes("Store Configuration Category_Create") ? (
              <ActionButton
                text="New+"
                newBtn="true"
                onClick={() => history.push("/store/configuration/category/add")}
              />
            ) : (
              <span>Action</span>
            )}
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              gap: "12px",
            }}
          >
            {rolesData?.data?.UserRoles?.includes("Store Configuration Category_Edit") && (
              <FiEdit2
                title="Edit"
                size="1.1em"
                style={{ cursor: "pointer", color: "#4e545a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  history.push("/store/configuration/category/add", {
                    ...params.data,
                    isEdit: true,
                  });
                }}
              />
            )}
            {rolesData?.data?.UserRoles?.includes("Store Configuration Category_Delete") && (
              <MdDeleteForever
                title="Delete"
                size="1.1em"
                className="cur_sor"
                style={{ cursor: "pointer", color: "#c0392b" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCategory(params.data);
                }}
              />
            )}
          </div>
        ),
      },
    ],
    [history, rolesData],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 100,
    }),
    [],
  );

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Category"
        >
          Category
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
        />
        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
        {categoryData?.loading && (
          <div>
            <ThreeDots color="green" height={40} width={40} />
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredCategories || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        open={!!selectedCategory}
        onClose={() => setSelectedCategory(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        message={`Are you sure you want to delete category "${selectedCategory?.CategoryName}"?`}
        heading="Delete Category"
      />
    </>
  );
};

export { CategoryTable };
export default CategoryTable;
